"use server";

import { apiFetchServer } from "@/src/lib/api/fetch.server";
import { MARKETING_DIECUTS_PAGE_SIZE } from "@/src/constants/marketing-diecuts";

export interface MarketingDiecutListItem {
  id: string;
  slug: string;
  name: string | null;
  categoryTitle: string | null;
  categoryId: number;
  thumbnailUrl: string | null;
  materialName: string | null;
  length: string | null;
  width: string | null;
  height: string | null;
  dimensionUnit: "in" | "mm" | "cm";
  complexity: "simple" | "medium" | "complex" | null;
  isFeatured: boolean;
  tags: string[] | null;
}

export interface MarketingDiecutDetail extends MarketingDiecutListItem {
  description: string | null;
  knifeDataUrl: string | null;
  isCustomDimensions: boolean;
  default3dRotW: number | null;
  default3dRotX: number | null;
  default3dRotY: number | null;
  default3dRotZ: number | null;
  default3dDistance: number | null;
}

type BackendPublicDiecut = {
  id: string;
  slug: string;
  name?: string | null;
  materialName?: string | null;
  categoryTitle?: string | null;
  categoryId?: string | number;
  categorySlug?: string | null;
  thumbnailUrl?: string | null;
  length?: string | number | null;
  width?: string | number | null;
  height?: string | number | null;
  dimensionUnit: "in" | "mm" | "cm";
  complexity?: "simple" | "medium" | "complex" | null;
  isFeatured: boolean;
  isCustomDimensions?: boolean;
  tags?: string | null;
  description?: string | null;
  knifeDataUrl?: string | null;
  default3dRotW?: number | null;
  default3dRotX?: number | null;
  default3dRotY?: number | null;
  default3dRotZ?: number | null;
  default3dDistance?: number | null;
};

type PaginatedGallery = {
  data: BackendPublicDiecut[];
  meta?: { total: number; page: number; limit: number };
};

function parseTags(tags: string | null | undefined): string[] | null {
  if (!tags) return null;
  try {
    const parsed = JSON.parse(tags) as string[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function mapListItem(row: BackendPublicDiecut): MarketingDiecutListItem {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name ?? null,
    categoryTitle: row.categoryTitle ?? null,
    categoryId: Number.parseInt(String(row.categoryId ?? "0"), 10) || 0,
    thumbnailUrl: row.thumbnailUrl ?? null,
    materialName: row.materialName ?? row.categoryTitle ?? null,
    length: row.length != null ? String(row.length) : null,
    width: row.width != null ? String(row.width) : null,
    height: row.height != null ? String(row.height) : null,
    dimensionUnit: row.dimensionUnit,
    complexity: row.complexity ?? null,
    isFeatured: row.isFeatured,
    tags: parseTags(row.tags),
  };
}

export interface MarketingDiecutsPageParams {
  page: number;
  category?: string;
  search?: string;
}

export interface MarketingDiecutsPage {
  items: MarketingDiecutListItem[];
  total: number;
  page: number;
  limit: number;
}

export async function getMarketingDiecutsPage({
  page,
  category,
  search,
}: MarketingDiecutsPageParams): Promise<MarketingDiecutsPage> {
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(MARKETING_DIECUTS_PAGE_SIZE),
  });
  if (category) qs.set("category", category);
  if (search) qs.set("search", search);
  const res = await apiFetchServer<PaginatedGallery>(
    `/public/diecuts/gallery?${qs.toString()}`,
    { skipAuth: true },
  );
  return {
    items: res.data.map(mapListItem),
    total: res.meta?.total ?? res.data.length,
    page: res.meta?.page ?? page,
    limit: res.meta?.limit ?? MARKETING_DIECUTS_PAGE_SIZE,
  };
}

export async function getMarketingDiecuts(): Promise<MarketingDiecutListItem[]> {
  const { items } = await getMarketingDiecutsPage({ page: 1 });
  return items;
}

export async function getMarketingDiecutBySlug(
  slug: string,
): Promise<MarketingDiecutDetail | null> {
  try {
    const row = await apiFetchServer<BackendPublicDiecut>(
      `/public/diecuts/${slug}`,
      { skipAuth: true },
    );
    return {
      ...mapListItem(row),
      description: row.description ?? null,
      knifeDataUrl: row.knifeDataUrl ?? null,
      isCustomDimensions: row.isCustomDimensions ?? false,
      default3dRotW: row.default3dRotW ?? null,
      default3dRotX: row.default3dRotX ?? null,
      default3dRotY: row.default3dRotY ?? null,
      default3dRotZ: row.default3dRotZ ?? null,
      default3dDistance: row.default3dDistance ?? null,
    };
  } catch {
    return null;
  }
}

export async function getMarketingRelatedDiecuts(
  categoryId: number,
  excludeSlug: string,
  limit = 3,
): Promise<MarketingDiecutListItem[]> {
  const all = await getMarketingDiecuts();
  return all
    .filter((d) => d.categoryId === categoryId && d.slug !== excludeSlug)
    .slice(0, limit);
}
